import { useState, useEffect, useRef } from 'react';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { graphqlClient } from '../../../lib/graphql-client';
import { useAuthStore } from '../../../state/store/auth';

const MEDIA_UPLOAD_URL = 'https://api.seliseblocks.com/media/v1/media/upload';
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';

const INSERT_PAGE_LAYOUT = `
  mutation InsertPageLayout($input: PageLayoutInsertInput!) {
    insertPageLayout(input: $input) {
      acknowledged
      itemId
      message
    }
  }
`;

const GET_PAGE_LAYOUTS = `
  query GetPageLayouts {
    pageLayouts {
      items {
        id
        pageId
        pageName
        layout
      }
    }
  }
`;

const DELETE_PAGE_LAYOUT = `
  mutation DeletePageLayout($id: String!) {
    deletePageLayout(id: $id) {
      acknowledged
      message
    }
  }
`;

const COMPONENTS = [
  { type: 'hero', label: '🧑 Hero Section', defaultProps: { title: 'Welcome!', subtitle: 'Your subtitle here', bg: '#1e40af' } },
  { type: 'text', label: '📝 Text Block', defaultProps: { content: 'Your text here...' } },
  { type: 'image', label: '🖼️ Image Block', defaultProps: { url: 'https://via.placeholder.com/600x300', alt: 'Image' } },
  { type: 'button', label: '🔘 Button', defaultProps: { label: 'Click Me', color: '#2563eb' } },
];

function SortableBlock({ block, onUpdate, onDelete }: any) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    setUploadError('');
    try {
      const token = useAuthStore.getState().accessToken;
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(MEDIA_UPLOAD_URL, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `bearer ${token}` }),
          ...(projectKey && { 'x-blocks-key': projectKey }),
        },
        body: formData,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const json = await res.json();
      const url = json?.url || json?.data?.url || json?.fileUrl || json?.data?.fileUrl;
      if (!url) throw new Error('No URL in response');
      onUpdate(block.id, { ...block.props, url });
    } catch (e: any) {
      setUploadError(e.message || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <div ref={setNodeRef} style={style} className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-3 bg-white group hover:border-blue-400">
      <div className="flex justify-between items-center mb-2">
        <span {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600">⠿ {block.type.toUpperCase()}</span>
        <button onClick={() => onDelete(block.id)} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100">x</button>
      </div>
      {block.type === 'hero' && (
        <div style={{ background: block.props.bg }} className="p-6 rounded text-white">
          <input className="bg-transparent text-2xl font-bold w-full outline-none" value={block.props.title} onChange={e => onUpdate(block.id, { ...block.props, title: e.target.value })} />
          <input className="bg-transparent text-sm w-full outline-none mt-1" value={block.props.subtitle} onChange={e => onUpdate(block.id, { ...block.props, subtitle: e.target.value })} />
        </div>
      )}
      {block.type === 'text' && (
        <textarea className="w-full border rounded p-2 text-gray-700 outline-none" rows={3} value={block.props.content} onChange={e => onUpdate(block.id, { ...block.props, content: e.target.value })} />
      )}
      {block.type === 'image' && (
        <div>
          {block.props.url && <img src={block.props.url} alt={block.props.alt} className="w-full rounded mb-2" />}
          <input className="w-full border rounded p-1 text-sm mb-2" placeholder="Image URL" value={block.props.url} onChange={e => onUpdate(block.id, { ...block.props, url: e.target.value })} />
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ''; }} />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="w-full py-1.5 text-sm border border-blue-400 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50">
            {uploading ? '⏳ Uploading...' : '📁 Upload from computer'}
          </button>
          {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
        </div>
      )}
      {block.type === 'button' && (
        <button style={{ background: block.props.color }} className="px-6 py-2 rounded text-white font-medium">
          <input className="bg-transparent outline-none text-white" value={block.props.label} onChange={e => onUpdate(block.id, { ...block.props, label: e.target.value })} />
        </button>
      )}
    </div>
  );
}

export default function EditorPage() {
  const [blocks, setBlocks] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [pageName, setPageName] = useState('Home');
  const [pages, setPages] = useState<any[]>([]);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [loadingPages, setLoadingPages] = useState(true);
  const [showNewPage, setShowNewPage] = useState(false);
  const [newPageName, setNewPageName] = useState('');

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoadingPages(true);
    try {
      const result = await graphqlClient.query<any>({ query: GET_PAGE_LAYOUTS });
      const items = (result?.pageLayouts?.items || []).map((item: any) => {
        try {
          const parsed = JSON.parse(item.layout || '{}');
          return { ...item, pageName: parsed.pageName || item.pageName || item.pageId };
        } catch {
          return { ...item, pageName: item.pageName || item.pageId };
        }
      });
      setPages(items);
    } catch (e) {
      console.error('Error loading pages', e);
    }
    setLoadingPages(false);
  };

  const loadPage = (page: any) => {
    setCurrentPageId(page.id);
    const parsed = JSON.parse(page.layout || '{}');
    setPageName(parsed.pageName || page.pageName || page.pageId);
    setBlocks(parsed.blocks || []);
    setSaveStatus('');
  };

  const savePage = async (name: string, blks: any[], pid: string | null): Promise<boolean> => {
    try {
      const result = await graphqlClient.mutate<any>({
        query: INSERT_PAGE_LAYOUT,
        variables: {
          input: {
            pageId: pid ?? `page-${Date.now()}`,
            layout: JSON.stringify({ pageName: name, blocks: blks }),
          },
        },
      });
      return !!result?.insertPageLayout?.acknowledged;
    } catch (e) {
      console.error('Error saving page', e);
      return false;
    }
  };

  const newPage = async () => {
    const name = newPageName.trim() || 'New Page';
    setShowNewPage(false);
    setNewPageName('');
    if (blocks.length > 0) {
      setSaving(true);
      setSaveStatus('Saving...');
      await savePage(pageName, blocks, currentPageId);
      await loadPages();
      setSaving(false);
    }
    setCurrentPageId(null);
    setPageName(name);
    setBlocks([]);
    setSaveStatus('');
  };

  const deletePage = async (id: string) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await graphqlClient.mutate<any>({ query: DELETE_PAGE_LAYOUT, variables: { id } });
      await loadPages();
      if (currentPageId === id) {
        setCurrentPageId(null);
        setBlocks([]);
        setPageName('Home');
      }
    } catch (e) {
      console.error('Error deleting page', e);
    }
  };

  const addBlock = (type: string) => {
    const comp = COMPONENTS.find(c => c.type === type)!;
    setBlocks(prev => [...prev, { id: `${type}-${Date.now()}`, type, props: { ...comp.defaultProps } }]);
  };

  const updateBlock = (id: string, props: any) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, props } : b));
  };

  const deleteBlock = (id: string) => {
    setBlocks(prev => prev.filter(b => b.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setBlocks(prev => {
        const oldIndex = prev.findIndex(b => b.id === active.id);
        const newIndex = prev.findIndex(b => b.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const saveLayout = async () => {
    setSaving(true);
    setSaveStatus('Saving...');
    const ok = await savePage(pageName, blocks, currentPageId);
    if (ok) {
      setSaveStatus('✅ Saved!');
      await loadPages();
    } else {
      setSaveStatus('❌ Error saving');
    }
    setSaving(false);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left sidebar - Components + Pages */}
      <div className="w-64 bg-white shadow-lg p-4 flex flex-col overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-800 mb-4">🎨 Components</h2>
        {COMPONENTS.map(comp => (
          <button key={comp.type} onClick={() => addBlock(comp.type)}
            className="w-full text-left px-3 py-2 mb-2 rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 transition-colors">
            {comp.label}
          </button>
        ))}

        <hr className="my-4" />

        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-bold text-gray-800">📄 My Pages</h2>
          <button onClick={() => setShowNewPage(true)} className="text-blue-600 hover:text-blue-800 text-xl font-bold">+</button>
        </div>

        {showNewPage && (
          <div className="mb-2">
            <input value={newPageName} onChange={e => setNewPageName(e.target.value)}
              placeholder="Page name" className="w-full border rounded p-1 text-sm mb-1" />
            <button onClick={newPage} className="w-full bg-blue-600 text-white text-sm py-1 rounded">Create</button>
          </div>
        )}

        {loadingPages ? <p className="text-sm text-gray-400">Loading...</p> : pages.length === 0 ? (
          <p className="text-xs text-gray-400 px-2">No pages yet</p>
        ) : (
          pages.map(page => (
            <div key={page.id}
              className={`group flex items-center justify-between px-2 py-1.5 mb-0.5 rounded-md cursor-pointer transition-colors ${currentPageId === page.id ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}>
              <span onClick={() => loadPage(page)} className="flex items-center gap-1.5 text-sm truncate flex-1 min-w-0">
                <span className="shrink-0">📄</span>
                <span className="truncate">{page.pageName || page.pageId}</span>
              </span>
              <button onClick={() => deletePage(page.id)}
                className="shrink-0 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs ml-1">
                ✕
              </button>
            </div>
          ))
        )}

        <div className="mt-auto">
          <input value={pageName} onChange={e => setPageName(e.target.value)}
            className="w-full border rounded p-2 text-sm mb-2" placeholder="Page name" />
          {saveStatus && <p className="text-sm text-center mb-2 text-xs">{saveStatus}</p>}
          <button onClick={saveLayout} disabled={saving}
            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
            {saving ? '⏳ Saving...' : '💾 Save to SELISE'}
          </button>
        </div>
      </div>

      {/* Main canvas */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">✏️ Page Editor — {pageName}</h1>
          {blocks.length === 0 && (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-400">
              <p className="text-4xl mb-2">+</p>
              <p>Click components on the left to add them here</p>
            </div>
          )}
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              {blocks.map(block => (
                <SortableBlock key={block.id} block={block} onUpdate={updateBlock} onDelete={deleteBlock} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
}
